// 邀请码状态枚举
export enum InviteCodeStatus {
  INVITE_CODE_STATUS_UNSPECIFIED = 0, // 未指定
  INVITE_CODE_STATUS_ACTIVE = 1, // 可用
  INVITE_CODE_STATUS_USED = 2, // 已使用
  INVITE_CODE_STATUS_EXPIRED = 3, // 已过期
  INVITE_CODE_STATUS_DELETED = 4, // 已删除（软删除）
}

// 邀请码实体
export interface InviteCode {
  id: string; // 邀请码唯一标识
  code: string; // 邀请码字符串
  status: InviteCodeStatus; // 状态
  created_at: string; // 创建时间 (ISO 8601)
  used_at?: string; // 使用时间（可选）(ISO 8601)
  used_by?: string; // 使用者用户ID（可选）
  expires_at?: string; // 过期时间（可选）(ISO 8601)
  creator_uid?: string; // 创建者用户ID（可选）
}

// 创建邀请码请求
export interface CreateInviteCodeRequest {
  count: number; // 生成数量，默认为1
  expires_at?: string; // 过期时间（可选）(ISO 8601)
}

// 创建邀请码响应
export interface CreateInviteCodeReply {
  invite_codes: InviteCode[]; // 生成的邀请码列表
}

// 删除邀请码请求
export interface DeleteInviteCodeRequest {
  id: string; // 邀请码ID
}

// 删除邀请码响应
export interface DeleteInviteCodeReply {
  success: boolean; // 是否成功
  message?: string; // 消息（可选）
}

// 获取单个邀请码请求
export type GetInviteCodeRequest =
  | { id: string; code?: never }
  | { id?: never; code: string };

// 获取单个邀请码响应
export interface GetInviteCodeReply {
  invite_code: InviteCode; // 邀请码详情
}

// 列表查询邀请码请求
export interface ListInviteCodesRequest {
  page_num: number; // 兼容旧服务端：页码，从1开始
  page_size: number; // 每页数量
  page_token?: string; // 游标分页 token，服务端优先于 page_num
  keyword?: string; // 关键词搜索（可选，搜索code或username）
  status?: InviteCodeStatus; // 状态筛选（可选）
}

// 列表查询邀请码响应
export interface ListInviteCodesReply {
  invite_codes: InviteCode[]; // 邀请码列表
  total: number; // 总数
  next_page_token?: string; // 下一页游标；旧服务端可能不返回
  has_more?: boolean; // 是否还有下一页
}

// 消费邀请码请求
export interface ConsumeInviteCodeRequest {
  code: string;
  user_id: string;
}

// 消费邀请码响应
export interface ConsumeInviteCodeReply {
  success: boolean;
  message?: string;
  invite_code?: InviteCode;
}

export interface BetaAccess {
  id: string;
  user_id: string;
  grant_source: string;
  grant_ref_type?: string;
  grant_ref_id?: string;
  granted_by?: string;
  granted_at?: string;
  revoked_by?: string;
  revoked_at?: string;
  updated_at?: string;
  active: boolean;
  status: BetaAccessStatus;
}

export interface GrantBetaAccessRequest {
  user_id: string;
}

export interface GrantBetaAccessReply {
  beta_access: BetaAccess;
}

export enum BetaAccessStatus {
  BETA_ACCESS_STATUS_UNSPECIFIED = 0,
  BETA_ACCESS_STATUS_ACTIVE = 1,
  BETA_ACCESS_STATUS_REVOKED = 2,
}

export enum MailOutboxStatus {
  MAIL_OUTBOX_STATUS_UNSPECIFIED = 0,
  MAIL_OUTBOX_STATUS_PENDING = 1,
  MAIL_OUTBOX_STATUS_SENT = 2,
  MAIL_OUTBOX_STATUS_FAILED = 3,
}

export interface MailOutbox {
  id: string;
  biz_type: string;
  biz_id: string;
  template_code: string;
  to_email: string;
  payload: string;
  status: MailOutboxStatus;
  retry_count: number;
  provider_message_id?: string;
  last_error?: string;
  sent_at?: string;
  created_at?: string;
  updated_at?: string;
}

export interface WaitlistQuestionnaire {
  use_cases: string[];
  use_case_other_text?: string;
  roles: string[];
  role_other_text?: string;
  social_platform?: string;
  social_platform_other_text?: string;
  social_handle?: string;
  team_size?: string;
}

export interface RevokeBetaAccessRequest {
  user_id: string;
}

export interface RevokeBetaAccessReply {
  beta_access: BetaAccess;
}

export interface ListBetaAccessRequest {
  page_num: number;
  page_size: number;
  user_id?: string;
  status?: BetaAccessStatus;
  grant_source?: string;
}

export interface ListBetaAccessReply {
  beta_accesses: BetaAccess[];
  total: number;
}

// 邀请申请状态枚举
export enum InviteApplicationStatus {
  INVITE_APPLICATION_STATUS_UNSPECIFIED = 0, // 未指定
  INVITE_APPLICATION_STATUS_PENDING = 1, // 未处理
  INVITE_APPLICATION_STATUS_EMAIL_SENT = 2, // 已发送邮件
  INVITE_APPLICATION_STATUS_REJECTED = 3, // 已拒绝
}

// 邀请码申请实体
export interface InviteCodeApplication {
  id: string;
  email: string;
  reason: string;
  status: InviteApplicationStatus;
  invite_code_id?: string;
  created_at: string;
  updated_at: string;
  user_id?: string; // 提交申请的用户ID
  latest_mail_outbox?: MailOutbox;
  questionnaire?: WaitlistQuestionnaire;
  schema_version?: number;
}

// 列表查询申请请求
export interface ListInviteApplicationsRequest {
  page_num: number;
  page_size: number;
  status?: InviteApplicationStatus;
}

// 列表查询申请响应
export interface ListInviteApplicationsReply {
  applications: InviteCodeApplication[];
  total: number;
}

// 审批并发送邀请码请求
export interface ApproveAndSendInviteRequest {
  id: string; // 申请ID
  invite_code_id?: string; // 可选，使用已有邀请码ID
}

// 审批并发送邀请码响应
export interface ApproveAndSendInviteReply {
  application: InviteCodeApplication;
}

// 单条发送邀请码请求
export interface SendInviteCodeRequest {
  id: string;
  to_email: string;
}

// 单条发送邀请码响应
export interface SendInviteCodeReply {
  success: boolean;
  message: string;
  outbox?: MailOutbox;
}

// 批量发送单项结果
export interface BatchSendResult {
  email: string;
  code: string;
  success: boolean;
  error: string;
}

// 批量发送请求
export interface BatchSendInviteCodesRequest {
  emails: string[];
  expires_at?: string;
}

// 批量发送响应
export interface BatchSendInviteCodesReply {
  results: BatchSendResult[];
  sent_count: number;
  failed_count: number;
}
